import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLConsolidationQuery } from "store/services/consolidationApi";
import { useLazyGetDDLLoggedInDriverQuery } from "store/services/driverApi";
import { useLazyGetDDLConsolidationShipmentsQuery } from "store/services/shipmentLoadingApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useAuth } from "hooks";
import { getDateTimeAuto } from "utils/dateFormats";

const INITIAL_STATE = {
  account: null,
  entity: null,
  consolidationId: null,
  shipmentId: [],
  vehicle_id: null,
  driverId: null,
  loadedBranch: null,
  loadingTime: getDateTimeAuto(),
  loadingLocation: { lat: "", lon: "" },
  isActive: true,
};

const DriverShipmentLoadingForm = ({
  isBusy = null,
  shipmentLoadingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
      consolidationId: state?.consolidation || null,
      shipmentId: state?.shipments || [],
    },
  });

  // Watch form fields
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchConsolidationId = useWatch({ control, name: "consolidationId" });
  const _watchDriver = useWatch({ control, name: "driverId" });

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [
    fetchConsolidations,
    { data: consolidations, isLoading: loadingConsolidations },
  ] = useLazyGetDDLConsolidationQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLLoggedInDriverQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLConsolidationShipmentsQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // Derived state for vehicle options
  const selectedVehicles = _watchDriver?.assigned_vehicle_ids || [];

  const selectedConsolidation = consolidations?.find(
    (cons) => cons?._id === _watchConsolidationId?._id
  );

  const consolidationShipments = React.useMemo(() => {
    if (selectedConsolidation) {
      return selectedConsolidation.shipmentId || [];
    } else if (state?.shipments?.length) {
      return state.shipments;
    } else {
      return shipments || [];
    }
  }, [selectedConsolidation, state?.shipments, shipments]);

  // Auto-populate driver and vehicle when drivers are fetched
  useEffect(() => {
    if (drivers && drivers.length > 0 && !shipmentLoadingDetail) {
      const firstDriver = drivers[0];

      // Auto-populate driver
      setValue("driverId", firstDriver);

      // Auto-populate first vehicle if available
      if (
        firstDriver.assigned_vehicle_ids &&
        firstDriver.assigned_vehicle_ids.length > 0
      ) {
        setValue("vehicle_id", firstDriver.assigned_vehicle_ids[0]);
      }
    }
  }, [drivers, shipmentLoadingDetail, setValue]);

  // Auto-populate consolidationId and shipmentId from state
  useEffect(() => {
    if (state?.consolidation && consolidations?.length) {
      const matchingConsolidation = consolidations.find(
        (cons) => cons._id.toString() === state.consolidation._id.toString()
      );
      if (matchingConsolidation) {
        setValue("consolidationId", matchingConsolidation);
      } else {
        setValue("consolidationId", state.consolidation);
      }
    }
  }, [state, consolidations, setValue]);

  // Fetch drivers and shipments based on account and entity
  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
    fetchShipments({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
      status: "Consolidation",
    });
  }, [_watchAccount?._id, _watchEntity?._id, fetchDrivers, fetchShipments]);

  // Auto-populate branches based on account and entity
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches]);

  // Methods
  const handleOnLoadingSubmit = (data) => {
    const _loadingPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      consolidationId: data?.consolidationId?._id || "",
      shipmentId: Array.isArray(data?.shipmentId)
        ? data?.shipmentId.map((id) => id._id)
        : [],
      vehicle_id: data?.vehicle_id?._id,
      driverId: data?.driverId?._id,
      loadedBranch: data?.loadedBranch?._id || null,
    };
    onSubmit(_loadingPayload);
  };

  useEffect(() => {
    if (_watchConsolidationId && selectedConsolidation && !state?.shipments) {
      const collectedShipmentIds = selectedConsolidation.shipmentId.map(
        (shipment) => ({
          _id: shipment._id,
          shipmentId: shipment.shipmentId,
          IsReturning: shipment.IsReturning,
        })
      );
      setValue("shipmentId", collectedShipmentIds);
    } else if (!_watchConsolidationId && !state?.shipments) {
      setValue("shipmentId", []);
    }
  }, [_watchConsolidationId, selectedConsolidation, state, setValue]);

  // Reset form when editing
  useEffect(() => {
    if (!shipmentLoadingDetail) return;

    const consolidationIdValue =
      shipmentLoadingDetail?.consolidationId?._id ||
      shipmentLoadingDetail?.consolidationId ||
      null;

    reset({
      ...INITIAL_STATE,
      ...shipmentLoadingDetail,
      loadingTime: shipmentLoadingDetail?.loadingTime
        ? new Date(shipmentLoadingDetail.loadingTime).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      consolidationId: consolidationIdValue
        ? { _id: consolidationIdValue }
        : null,
      shipmentId:
        shipmentLoadingDetail?.shipmentId?.map((id) =>
          typeof id === "string" ? { _id: id } : id
        ) || [],
      account: shipmentLoadingDetail?.account || null,
      entity: shipmentLoadingDetail?.entity || null,
      vehicle_id: shipmentLoadingDetail?.vehicle_id || null,
      driverId: shipmentLoadingDetail?.driverId || null,
      loadedBranch: shipmentLoadingDetail?.loadedBranch || null,
    });

    const accountId =
      shipmentLoadingDetail?.account?._id ||
      shipmentLoadingDetail?.account ||
      "";
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    const entityId =
      shipmentLoadingDetail?.entity?._id || shipmentLoadingDetail?.entity || "";
    if (entityId) {
      fetchConsolidations({ account: accountId, entity: entityId });
      fetchShipments({
        account: accountId,
        entity: entityId,
        status: "Consolidation",
      });
      fetchDrivers({ account: accountId, entity: entityId });
    }
  }, [
    shipmentLoadingDetail,
    reset,
    fetchEntities,
    fetchConsolidations,
    fetchShipments,
    fetchDrivers,
    isAccountAdmin,
    isSystemAdmin,
  ]);

  // Update consolidationId when consolidations are fetched
  useEffect(() => {
    if (shipmentLoadingDetail && consolidations) {
      const consolidationIdToMatch =
        shipmentLoadingDetail?.consolidationId?._id ||
        shipmentLoadingDetail?.consolidationId ||
        null;

      if (!consolidationIdToMatch) {
        setValue("consolidationId", null);
        return;
      }

      const matchingConsolidation = consolidations.find(
        (cons) => cons._id === consolidationIdToMatch
      );
      if (matchingConsolidation) {
        setValue("consolidationId", matchingConsolidation);
      } else {
        setValue("consolidationId", {
          _id: consolidationIdToMatch,
          consolidationId: `Unknown (${consolidationIdToMatch})`,
        });
      }
    }
  }, [shipmentLoadingDetail, consolidations, setValue]);

  // Fetch entities when account changes
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  // Fetch shipments and consolidations when entity changes
  useEffect(() => {
    if (!_watchEntity?._id || shipmentLoadingDetail) return;
    fetchShipments({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
      status: "Consolidation",
    });
    fetchConsolidations({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
      status: "Consolidation",
    });
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    fetchShipments,
    fetchConsolidations,
    fetchDrivers,
    shipmentLoadingDetail,
  ]);

  return (
    <>
      <AppForm loading={isBusy}>
        <AppForm.Content>
          <Grid container spacing={4}>
            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("entity", null);
                        setValue("consolidationId", null);
                        setValue("shipmentId", []);
                        setValue("driverId", null);
                        setValue("vehicle_id", null);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
            )}
            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Entity"
                      value={value ?? null}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("consolidationId", null);
                        setValue("shipmentId", []);
                        setValue("driverId", null);
                        setValue("vehicle_id", null);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            )}
            {!shipmentLoadingDetail && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="consolidationId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Consolidation"
                      value={value ?? null}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) =>
                        option?.consolidationId && option?.name
                          ? `${option.consolidationId} - ${option.name}`
                          : option?.consolidationId || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={consolidations || []}
                      loading={loadingConsolidations}
                      loadingText="Loading consolidations..."
                      helperText={
                        errors?.consolidationId?.message ||
                        (!loadingConsolidations &&
                          (!consolidations || consolidations.length === 0)
                          ? "No consolidations available"
                          : "")
                      }
                      error={!!errors?.consolidationId}
                      disabled={!!state?.consolidation || loadingConsolidations}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Controller
                name="shipmentId"
                control={control}
                rules={{ required: "Please select at least one Shipment ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Shipment ID"
                    required
                    value={value || []}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option?.shipmentId
                        ? option.IsReturning
                          ? `${option.shipmentId} (R)`
                          : option.shipmentId
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={consolidationShipments}
                    loading={loadingShipments}
                    loadingText="Loading Shipments..."
                    helperText={errors?.shipmentId?.message}
                    error={!!errors?.shipmentId}
                    disabled={loadingShipments}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="loadingTime"
                control={control}
                rules={{ required: "Loading Time is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Loading Time"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.loadingTime}
                    helperText={errors?.loadingTime?.message}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="driverId"
                control={control}
                rules={{ required: "Please select Driver ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Driver"
                    required
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicle_id", null);
                    }}
                    getOptionLabel={(option) =>
                      option && `${option?.driverId} - ${option?.name}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={drivers || []}
                    loading={loadingDrivers}
                    loadingText="Loading Drivers..."
                    helperText={errors?.driverId?.message}
                    error={!!errors?.driverId}
                    disabled={true} // Always disabled as it's auto-populated
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="vehicle_id"
                control={control}
                rules={{ required: "Please select Vehicle ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Vehicle"
                    required
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option && option.vehicle_id && option.registration_number
                        ? `${option.vehicle_id} - ${option.registration_number}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={selectedVehicles}
                    loadingText="Loading Vehicles..."
                    helperText={errors?.vehicle_id?.message}
                    error={!!errors?.vehicle_id}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="loadedBranch"
                control={control}
                rules={{ required: "Please select the loaded branch." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Loaded Branch"
                    required
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option
                        ? `${option?.name ?? ""} (${option?.code ?? ""})`.trim()
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={branches || []}
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    helperText={errors?.loadedBranch?.message}
                    error={!!errors?.loadedBranch}
                  />
                )}
              />
            </Grid>
            {shipmentLoadingDetail && (
              <Grid item xs={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <AppSwitch {...field} label="Is Active?" />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </AppForm.Content>
        <AppForm.Actions>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleOnLoadingSubmit)}
          >
            {shipmentLoadingDetail ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
    </>
  );
};

export default DriverShipmentLoadingForm;
